import { createTRPCReact, httpBatchLink } from '@trpc/react-query';
import { QueryClient } from '@tanstack/react-query';
import type { AppRouter } from '../../../server/src/routers/index.js';
import { getAccessToken } from './auth';

/**
 * tRPC React client
 * Provides typed hooks for all backend procedures
 */
export const trpc = createTRPCReact<AppRouter>();

/**
 * React Query client with good defaults
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time of 1 minute - data considered fresh for this duration
      staleTime: 1000 * 60,
      // Cache time of 5 minutes (cacheTime in v4)
      cacheTime: 1000 * 60 * 5,
      // Retry failed queries up to 3 times
      retry: 3,
      // Exponential backoff for retries
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Don't refetch on window focus by default
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
});

/**
 * tRPC client configuration
 * Points to backend at /api/trpc with automatic auth token handling
 */
export const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: '/api/trpc',
      /**
       * Add Authorization header from localStorage token
       */
      headers() {
        const token = getAccessToken();
        return token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {};
      },
    }),
  ],
});

// Note: In tRPC v10, hooks are accessed via the trpc object directly:
// e.g., trpc.auth.login.useMutation() instead of useMutation('auth.login')

/**
 * Type exports for use in components
 */
export type { AppRouter };
